import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Zap } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { adminSession } from '@/api/admin';
import { ApiError } from '@/lib/http';
import { toast } from '@/components/ui/sonner';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * 时段「立即生效」弹窗 — 绕过次日 03:05,强制把 session.* 待生效变更立即提前应用。
 * 需 reason(≥4 字),与品种「立即应用」对称(features/symbols/ApplyPendingNowDialog)。
 *
 * 重要:立即生效只改变**时段闸门判定**(下单/跟单/WS 实时读 system_config);
 * 强平等定时任务按服务端固定时刻调度,不随此变更改变(见设计 §9.10)。
 */
export function SessionApplyNowDialog({ open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) {
      setReason('');
    }
  }, [open]);

  const submit = useMutation({
    mutationFn: () => adminSession.applyPendingNow({ reason: reason.trim() }),
    onSuccess: (res) => {
      toast.success('时段配置已立即生效', {
        description: `已应用 ${res.appliedKeys.length} 项;闸门判定即时切换,强平等定时任务时刻不变`,
      });
      qc.invalidateQueries({ queryKey: ['admin', 'trading-session'] });
      onOpenChange(false);
    },
    onError: (err) => {
      toast.error('立即生效失败', { description: err instanceof ApiError ? err.message : '未知错误' });
    },
  });

  const reasonOk = reason.trim().length >= 4;
  const canSubmit = reasonOk && !submit.isPending;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !submit.isPending && onOpenChange(false)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-warn">
            <Zap className="h-5 w-5" />
            立即生效 · 时段配置
          </DialogTitle>
          <DialogDescription>
            正常修改于次日 03:05 生效,本操作绕过该机制立即生效。注意:仅时段闸门判定(开仓 / 收尾 /
            锁单)即时切换;强平等定时任务按服务端固定时刻执行,不随此变更改变。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="sess-apn-reason">操作原因(至少 4 字)</Label>
            <Input
              id="sess-apn-reason"
              placeholder="例如:今晚提前切换新交易时段"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={submit.isPending}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submit.isPending}>
            取消
          </Button>
          <Button variant="warn" onClick={() => submit.mutate()} disabled={!canSubmit}>
            {submit.isPending ? '生效中...' : '立即生效'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
